import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardHeaderProfileWidget from "../components/widgets/DashboardHeaderProfileWidget";
import {
  loadCourseDocuments,
  loadDegrees,
  loadProfile,
  loadQuestions,
  saveQuestionDegree,
} from "../utils/Network";
import { getDefaultName, getDefaultValue, isDocument, isImage, isVideo } from "../utils";
import { resolveMediaUrl } from "../utils/Network";
import Modal from "react-modal";
import UrlEmbed from "../components/UrlEmbed";
import { useStateValue } from "../StateProvider";
import "./RefereeJury.css";

function participantProfile(item) {
  return item?.userdocument?.usertalent?.user?.profile;
}

function participantName(item) {
  const p = participantProfile(item);
  if (!p) return "—";
  const n = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return n || p.email || "—";
}

function formatRate(item) {
  const r = item?.rate;
  if (r == null || r === "") return null;
  const n = Number(r);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n.toFixed(1);
}

function weightedRateFromQuestions(questions) {
  if (!questions || !questions.length) return 0;
  let degreeSum = 0;
  let weightSum = 0;
  questions.forEach((q) => {
    const w = Number(q.weight);
    const weight = Number.isFinite(w) && w > 0 ? w : 1;
    const d = Math.min(10, Math.max(0, Number(q.degree) || 0));
    degreeSum += d * weight;
    weightSum += weight;
  });
  if (weightSum === 0) return 0;
  return (degreeSum / weightSum) * 10;
}

/** Normalizes /rep/refereedegreesummary rows: [[{ question, degree }], ...] or legacy shapes */
function parseDegreeRows(degreesPayload) {
  if (!degreesPayload || !Array.isArray(degreesPayload)) return [];
  return degreesPayload
    .map((row) => {
      if (Array.isArray(row) && row[0] && row[0].question) return row[0];
      if (row && typeof row === "object" && row.question) return row;
      return null;
    })
    .filter(Boolean);
}

function degreeValueAndPkFromCell(cell) {
  if (!cell || cell.degree == null) return { value: 0, degreePk: null };
  const d = cell.degree;
  if (typeof d === "number") return { value: d, degreePk: cell.id ?? null };
  if (typeof d === "object") {
    return {
      value: d.degree != null ? Number(d.degree) : 0,
      degreePk: d.id != null ? d.id : null,
    };
  }
  return { value: 0, degreePk: null };
}

function mergeQuestionsWithDegrees(questions, degreesPayload) {
  if (!questions) return null;
  const cells = parseDegreeRows(degreesPayload);
  const byQ = new Map();
  cells.forEach((cell) => {
    const qid = cell.question?.id;
    if (qid == null) return;
    byQ.set(String(qid), cell);
  });
  return questions.map((q) => {
    const cell = byQ.get(String(q.id));
    const { value, degreePk } = cell
      ? degreeValueAndPkFromCell(cell)
      : { value: 0, degreePk: null };
    return {
      ...q,
      degree: String(Math.min(10, Math.max(0, value))),
      degree_id: degreePk,
    };
  });
}

export default function RefereePanel({ course, title, dates, profile, onProfileUpdated }) {
  const [ratingFilter, setRatingFilter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modal_file, setModalFile] = useState(null);
  const [modal_url, setModalUrl] = useState(null);
  const [modal_artist, setModalArtist] = useState(null);

  const [courseDocuments, setCourseDocuments] = useState(null);
  const [selectedCourseDocument, setSelectedCourseDocument] = useState(null);
  const [modalQuestions, setModalQuestions] = useState(null);
  const [refereeUserPk, setRefereeUserPk] = useState(null);
  const [modal_loading, setModalLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchDocuments = useCallback(() => {
    if (!course) {
      setCourseDocuments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    loadCourseDocuments(course, -1)
      .then((data) => {
        setCourseDocuments(data?.results ? [...data.results] : []);
        setLoading(false);
      })
      .catch(() => {
        setCourseDocuments([]);
        setLoading(false);
      });
  }, [course]);

  useEffect(() => {
    loadProfile().then((p) => {
      setRefereeUserPk(p?.user ?? p?.user_id ?? null);
    });
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const [{ translations }] = useStateValue();
  const translate = (str) => {
    if (!translations) return str;
    const tr = translations.find((x) => x.key === str);
    if (tr) return getDefaultValue(tr);
    return "#" + str;
  };

  const ratedCount = useMemo(
    () => (courseDocuments || []).filter((d) => d.is_rated).length,
    [courseDocuments]
  );
  const unratedCount = useMemo(
    () => (courseDocuments || []).filter((d) => !d.is_rated).length,
    [courseDocuments]
  );

  const filteredDocuments = useMemo(() => {
    if (!courseDocuments) return null;
    let list = courseDocuments;
    if (ratingFilter === 1) {
      list = list.filter((d) => d.is_rated);
      list = [...list].sort((a, b) => (Number(b.rate) || 0) - (Number(a.rate) || 0));
    } else if (ratingFilter === 0) {
      list = list.filter((d) => !d.is_rated);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((d) => participantName(d).toLowerCase().includes(q));
    }
    return list;
  }, [courseDocuments, searchQuery, ratingFilter]);

  const filterOptions = [
    { value: -1, label: translate("All"), count: courseDocuments ? courseDocuments.length : null },
    { value: 1, label: translate("Rated"), count: courseDocuments ? ratedCount : null },
    { value: 0, label: translate("Unrated"), count: courseDocuments ? unratedCount : null },
  ];

  const openModal = (courseDocument, file, url) => {
    if (url === "undefined") url = null;
    if (!refereeUserPk) return;

    const ut = courseDocument?.userdocument?.usertalent;
    const talentId = ut?.talent?.id ?? ut?.talent_id;
    const userId = ut?.user_id ?? ut?.user?.id;
    const courseId = courseDocument?.course?.id;
    if (!ut || talentId == null || userId == null || courseId == null) return;

    setModalFile(file);
    setModalUrl(url);
    setSelectedCourseDocument(courseDocument);
    setModalArtist(participantProfile(courseDocument));
    setModalQuestions(null);
    setModalLoading(true);
    setModalIsOpen(true);

    loadDegrees(courseId, userId, talentId, refereeUserPk)
      .then((degrees) => loadQuestions(talentId).then((questions) => {
        setModalQuestions(mergeQuestionsWithDegrees(questions || [], degrees));
      }))
      .catch(() => {
        setModalQuestions([]);
      })
      .finally(() => {
        setModalLoading(false);
      });
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const saveChanges = () => {
    if (!modalQuestions || !selectedCourseDocument || !refereeUserPk) return;

    const ut = selectedCourseDocument?.userdocument?.usertalent;
    const talentId = ut?.talent?.id ?? ut?.talent_id;
    const userId = ut?.user_id ?? ut?.user?.id;
    const courseId = selectedCourseDocument?.course?.id;
    if (talentId == null || userId == null || courseId == null) return;

    modalQuestions.forEach((question) => {
      saveQuestionDegree(
        question.degree_id,
        courseId,
        question.id,
        Number(question.degree),
        userId,
        talentId,
        refereeUserPk
      );
    });

    const savedId = selectedCourseDocument.id;
    const nextRate = weightedRateFromQuestions(modalQuestions);
    setCourseDocuments((prev) =>
      (prev || []).map((item) =>
        item.id === savedId ? { ...item, is_rated: true, rate: nextRate } : item
      )
    );
    closeModal();
  };

  if (!course) {
    return (
      <div className="referee-jury-empty">
        Choose a contest from your dashboard to review submissions.
      </div>
    );
  }

  return (
    <div>
      <Modal
        closeTimeoutMS={280}
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="Modal referee-rate-modal"
        ariaHideApp={false}
        overlayClassName="Overlay referee-rate-overlay"
      >
        <div className="referee-rate-modal__panel" key={selectedCourseDocument?.id || "closed"}>
          <header className="referee-rate-modal__header">
            <div className="referee-rate-modal__who">
              <img
                className="referee-rate-modal__avatar"
                alt=""
                src={
                  modal_artist && (modal_artist.avatar || modal_artist.image)
                    ? resolveMediaUrl(modal_artist.avatar || modal_artist.image)
                    : "/assets/images/user.png"
                }
              />
              <div className="referee-rate-modal__who-text">
                <h2 className="referee-rate-modal__kicker" id="defaultModalLabel">
                  {translate("Rate")}
                </h2>
              </div>
            </div>
            <button
              type="button"
              className="referee-rate-modal__close"
              onClick={closeModal}
              aria-label={translate("Close")}
            >
              <i className="fas fa-times" aria-hidden />
            </button>
          </header>

          <div className="referee-rate-modal__body">
            <div className="referee-rate-modal__media">
              {modal_url && <UrlEmbed url={modal_url} referee={true} />}
              {!modal_url && modal_file && isImage(modal_file) && (
                <img src={modal_file} alt="" />
              )}
              {!modal_url && modal_file && isVideo(modal_file) && (
                <video controls playsInline preload="metadata" src={modal_file} />
              )}
              {!modal_url && modal_file && isDocument(modal_file) && (
                <a
                  href={modal_file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="referee-rate-modal__doc"
                >
                  <img src="/assets/images/pdf_icon.png" alt="" />
                  <span>{translate("Open document")}</span>
                </a>
              )}
              {!modal_url && !modal_file && (
                <p className="referee-rate-modal__media-empty mb-0">
                  {translate("No media for this submission.")}
                </p>
              )}
            </div>

            <div className="referee-rate-modal__scores">
              <p className="referee-rate-modal__scores-label">{translate("Criteria")}</p>
              {modal_loading && (
                <div className="referee-rate-modal__loading">
                  <div className="bounce-loader">
                    <div className="bounce1" />
                    <div className="bounce2" />
                    <div className="bounce3" />
                  </div>
                </div>
              )}
              {!modal_loading && modalQuestions && modalQuestions.length === 0 && (
                <p className="referee-rate-modal__empty mb-0">
                  {translate("No scoring criteria for this talent.")}
                </p>
              )}
              {!modal_loading &&
                modalQuestions &&
                modalQuestions.map((item) => {
                  const v = Math.min(10, Math.max(0, Number(item.degree) || 0));
                  return (
                    <label key={item.id} className="referee-rate-modal__criterion">
                      <span className="referee-rate-modal__criterion-head">
                        <span className="referee-rate-modal__criterion-name">
                          {getDefaultName(item.names)}
                        </span>
                        <span className="referee-rate-modal__value">{v}</span>
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="1"
                        value={v}
                        aria-valuemin={0}
                        aria-valuemax={10}
                        aria-valuenow={v}
                        aria-label={getDefaultName(item.names)}
                        onChange={(e) => {
                          const next = e.target.value;
                          setModalQuestions((prev) =>
                            prev
                              ? prev.map((q) =>
                                  String(q.id) === String(item.id) ? { ...q, degree: next } : q
                                )
                              : prev
                          );
                        }}
                        className={`referee-rate-modal__range${v < 3 ? " is-low" : ""}`}
                        style={{ "--pct": `${v * 10}%` }}
                      />
                    </label>
                  );
                })}
            </div>
          </div>

          <footer className="referee-rate-modal__footer">
            <button
              type="button"
              className="referee-rate-modal__btn referee-rate-modal__btn--ghost kfont"
              onClick={closeModal}
            >
              {translate("Cancel")}
            </button>
            <button
              type="button"
              className="referee-rate-modal__btn referee-rate-modal__btn--save kfont"
              onClick={saveChanges}
              disabled={modal_loading}
            >
              {translate("Save")}
            </button>
          </footer>
        </div>
      </Modal>

      <div className={`referee-jury-pagehead${profile ? " referee-jury-pagehead--with-profile" : ""}`}>
        {profile && (
          <DashboardHeaderProfileWidget
            profile={profile}
            onProfileUpdated={onProfileUpdated}
            showManageProfile={false}
          />
        )}
        <div className="referee-jury-pagehead__controls">
          {(title || dates) && (
            <div className="referee-jury-pagehead__copy">
              {title ? <h2 className="referee-jury-pagehead__title">{title}</h2> : null}
              {dates ? <p className="referee-jury-pagehead__dates">{dates}</p> : null}
            </div>
          )}
          <div className="referee-jury-filters" role="group" aria-label={translate("Filter")}>
            {filterOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                aria-pressed={ratingFilter === opt.value}
                className={`referee-jury-chip${ratingFilter === opt.value ? " is-active" : ""}`}
                onClick={() => setRatingFilter(opt.value)}
              >
                {opt.label}
                {opt.count != null ? (
                  <span className="referee-jury-chip__count">{opt.count}</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="loader-container">
          <div className="bounce-loader">
            <div className="bounce1"></div>
            <div className="bounce2"></div>
            <div className="bounce3"></div>
          </div>
        </div>
      )}

      {!loading && filteredDocuments && filteredDocuments.length === 0 && (
        <div className="referee-jury-empty">
          {courseDocuments && courseDocuments.length > 0
            ? translate("No entries match your search.") || "No entries match your search."
            : translate("No submissions to show for this filter.") ||
              "No submissions to show for this filter."}
        </div>
      )}

      {!loading && filteredDocuments && filteredDocuments.length > 0 && (
        <div className="referee-jury-grid">
          {filteredDocuments.map((item) => {
            const p = participantProfile(item);
            const ud = item.userdocument;
            const file = ud?.file ? resolveMediaUrl(ud.file) : ud?.file;
            const url = ud?.url ? resolveMediaUrl(ud.url) : ud?.url;
            const rated = !!item.is_rated;
            const score = formatRate(item);
            const avatar = p?.avatar || p?.image || p?.thumb;

            let thumb = null;
            if (avatar) {
              thumb = <img src={resolveMediaUrl(avatar)} alt="" />;
            } else if (file && isImage(file)) {
              thumb = <img src={file} alt="" />;
            } else if (file && isVideo(file)) {
              thumb = (
                <video muted playsInline>
                  <source src={file} type="video/mp4" />
                </video>
              );
            } else if (file && isDocument(file)) {
              thumb = (
                <img src="/assets/images/pdf_icon.png" className="img-fluid border-radius-0 full-width" alt="" />
              );
            } else {
              thumb = <img src="/assets/images/user.png" alt="" />;
            }

            return (
              <div key={item.id} className="referee-jury-card-col">
                <button
                  type="button"
                  className={`referee-jury-card${rated ? " is-rated" : ""}`}
                  style={{ cursor: refereeUserPk ? "pointer" : "not-allowed" }}
                  onClick={() => refereeUserPk && openModal(item, file, url)}
                >
                  <div className="referee-jury-card-img-wrap">
                    {thumb}
                    {rated && score ? (
                      <span className="referee-jury-score-badge">{score}</span>
                    ) : null}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
